import * as XLSX from "xlsx";
import { getColumnWidths, getFeedbacksSheet, createInfoSheet } from "./common";
import * as _ from "lodash";
import {
  TInstancesMC,
  TFormattedResults,
  TMultipleCriteriaSession,
  TInstanceMC,
} from "types/multipleCriteria";

export const exportMultipleCriteriaResults = (
  session: TMultipleCriteriaSession,
  instances: TInstancesMC
) => {
  const excelFile = XLSX.utils.book_new();

  // Créer l'onglet d'informations générales
  const infoSheet = createInfoSheet(
    session.metadata.name,
    "Multiple Criteria",
    session.metadata.context
  );
  XLSX.utils.book_append_sheet(excelFile, infoSheet, "Information");

  const feedbacksSheet = getFeedbacksSheet(session, instances);
  const { formatedResults, criteriaStats } = getStatsData(session, instances);

  // Grouper les résultats par séquence
  const resultsBySequence = _.groupBy(formatedResults, "sequenceId");

  // Créer un onglet pour chaque séquence
  Object.keys(resultsBySequence).forEach((sequenceId, index) => {
    const sequenceResults = resultsBySequence[sequenceId];
    const resultsSheet = createResultsSheet(sequenceResults);

    if (resultsSheet) {
      XLSX.utils.book_append_sheet(
        excelFile,
        resultsSheet,
        `Sequence ${index + 1}`
      );
    }
  });

  // Créer l'onglet Statistiques
  const statisticsSheet = createStatisticsSheet(criteriaStats, session);
  if (statisticsSheet) {
    XLSX.utils.book_append_sheet(excelFile, statisticsSheet, "Statistics");
  }

  if (feedbacksSheet) {
    XLSX.utils.book_append_sheet(excelFile, feedbacksSheet, "Feedbacks");
  }

  XLSX.writeFile(excelFile, `export_session_${session.id}.xlsx`);
};

const createResultsSheet = (resutsPerSequences: TFormattedResults) => {
  if (!resutsPerSequences || resutsPerSequences.length === 0) {
    return null;
  }

  const excelRows = [];
  const allHeaders = [...Object.keys(resutsPerSequences[0] || {})];
  // Exclure sequenceId de l'affichage
  const filteredHeaders = allHeaders.filter(
    (header) => header !== "sequenceId"
  );

  // Définir l'ordre des colonnes : Participant en premier, puis Ressource, puis les critères
  const orderedHeaders: string[] = [];
  if (filteredHeaders.includes("Participant")) {
    orderedHeaders.push("Participant");
  }
  if (filteredHeaders.includes("Ressource")) {
    orderedHeaders.push("Ressource");
  }
  // Ajouter les autres colonnes (critères) dans l'ordre original
  filteredHeaders.forEach((header) => {
    if (header !== "Participant" && header !== "Ressource") {
      orderedHeaders.push(header);
    }
  });

  excelRows.push(orderedHeaders);

  resutsPerSequences.forEach((row: any) => {
    const rowData = orderedHeaders.map((header) => row[header] || "");
    excelRows.push(rowData);
  });

  const resultsSheet = XLSX.utils.aoa_to_sheet(excelRows);
  const colWidths = getColumnWidths(resutsPerSequences);
  resultsSheet["!cols"] = colWidths;

  return resultsSheet;
};

const createStatisticsSheet = (
  criteriaStats: any[],
  session: TMultipleCriteriaSession
) => {
  if (!criteriaStats || criteriaStats.length === 0) {
    return null;
  }

  const excelRows = [];

  // En-têtes de base
  const baseHeaders = [
    "Ressource",
    "Critère",
    "Average",
    "Median",
    "Standard deviation",
  ];

  // Ajouter les colonnes d'attributs
  const attributeHeaders =
    session.metadata.attributes?.map((attr) => `${attr.name} - ${attr.unit}`) ||
    [];
  const allHeaders = [...baseHeaders, ...attributeHeaders];

  excelRows.push(allHeaders);

  // Données
  criteriaStats.forEach((stat) => {
    const baseData = [
      stat.resourceName,
      stat.criteriaName,
      stat.average,
      stat.median,
      stat.standardDeviation,
    ];

    // Ajouter les valeurs des attributs
    const attributeValues =
      session.metadata.attributes?.map((attr, index) => {
        const resource = session.data.sequencesResources.find(
          (r) => r.name === stat.resourceName
        );
        return resource?.attributes?.[index]?.quantity || "";
      }) || [];

    const allData = [...baseData, ...attributeValues];
    excelRows.push(allData);
  });

  const statisticsSheet = XLSX.utils.aoa_to_sheet(excelRows);

  // Ajuster la largeur des colonnes
  const colWidths = [
    { width: 25 }, // Ressource
    { width: 20 }, // Critère
    { width: 15 }, // Average
    { width: 15 }, // Median
    { width: 15 }, // Standard deviation
  ];

  // Ajouter les largeurs pour les colonnes d'attributs
  attributeHeaders.forEach(() => {
    colWidths.push({ width: 20 });
  });

  statisticsSheet["!cols"] = colWidths;

  return statisticsSheet;
};

const getStatsData = (
  session: TMultipleCriteriaSession,
  instances: TInstancesMC
) => {
  const results = {};
  instances.map((instance: TInstanceMC) => _.merge(results, instance.results));

  const { data, metadata } = session;
  const { msCriterias = [] } = data;
  const { personalQuestions = [] } = metadata;
  const formatedResults: TFormattedResults = [];
  const criteriaStats: any[] = [];

  // Initialiser les statistiques pour chaque ressource et critère fermé uniquement
  data.sequencesResources.forEach((resource) => {
    msCriterias.forEach((criteria) => {
      // Ne prendre que les critères fermés (avec des scores numériques)
      if (criteria.type === "CLOSE") {
        criteriaStats.push({
          resourceId: resource.id,
          resourceName: resource.name,
          criteriaId: criteria.id,
          criteriaName: criteria.name,
          scores: [],
        });
      }
    });
  });

  _.keys(results).map((sequenceId) => {
    // @ts-ignore
    const sequenceResult = results[sequenceId];

    const participantsVote = _.omit(sequenceResult, "_fakeId");

    const ressourceId = session.data.sequences.find(
      (sequence) => sequence.id === sequenceId
    )?.resources[0].id;

    const ressourceName =
      session.data.sequencesResources.find(
        (resource) => resource.id === ressourceId
      )?.name || "";

    // For each participant vote
    for (let participantId in participantsVote) {
      const participant = instances?.find(
        (instance: TInstanceMC) =>
          instance.participants?.[0]?.id === participantId
      )?.participants?.[0];

      const participantName: string =
        participant?.infos &&
        typeof participant.infos === "object" &&
        "name" in participant.infos
          ? (participant.infos as any).name
          : participantId;

      const sequenceFormatedResult: any = {
        sequenceId: sequenceId,
        Participant: participantName,
        Ressource: ressourceName,
      };
      const vote = participantsVote[participantId];

      // Add criteria columns
      msCriterias.map(
        // @ts-ignore
        (criteria) => (sequenceFormatedResult[criteria.name] = null)
      );

      // For each criteria the participant has voted for
      for (let criteriaId in vote) {
        // @ts-ignore
        const criteriaName = msCriterias.find((c) => c.id === criteriaId).name;

        // @ts-ignore
        sequenceFormatedResult[criteriaName] = vote[criteriaId];

        // Collecter les scores pour les statistiques (critères fermés uniquement)
        criteriaStats.forEach((stat) => {
          if (
            stat.resourceId === ressourceId &&
            stat.criteriaId === criteriaId
          ) {
            const score = parseFloat(vote[criteriaId]);
            // Vérifier que c'est bien un nombre valide
            if (!isNaN(score)) {
              stat.scores.push(score);
            }
          }
        });
      }

      // Add personal questions columns
      personalQuestions.forEach((question) => {
        const instance = instances.find(
          (inst: TInstanceMC) => inst.participants?.[0]?.id === participantId
        );

        const answer = instance?.personalQuestions?.[question.id] || "";
        sequenceFormatedResult[question.question] = answer;
      });

      formatedResults.push(sequenceFormatedResult);
    }

    return null;
  });

  // Calculer les statistiques finales
  const finalStats = criteriaStats.map((stat) => {
    const scores = stat.scores;
    const average =
      scores.length > 0
        ? scores.reduce((sum: number, score: number) => sum + score, 0) /
          scores.length
        : 0;

    // Calcul de la médiane
    const sortedScores = [...scores].sort((a, b) => a - b);
    const median =
      sortedScores.length > 0
        ? sortedScores.length % 2 === 0
          ? (sortedScores[sortedScores.length / 2 - 1] +
              sortedScores[sortedScores.length / 2]) /
            2
          : sortedScores[Math.floor(sortedScores.length / 2)]
        : 0;

    // Calcul de l'écart-type
    const variance =
      scores.length > 0
        ? scores.reduce(
            (sum: number, score: number) => sum + Math.pow(score - average, 2),
            0
          ) / scores.length
        : 0;
    const standardDeviation = Math.sqrt(variance);

    return {
      resourceName: stat.resourceName,
      criteriaName: stat.criteriaName,
      average: Math.round(average * 100) / 100, // Arrondir à 2 décimales
      median: Math.round(median * 100) / 100, // Arrondir à 2 décimales
      standardDeviation: Math.round(standardDeviation * 100) / 100, // Arrondir à 2 décimales
    };
  });

  return { formatedResults, criteriaStats: finalStats };
};
